/**
 * Manga export — renders each page MANUALLY onto an HTMLCanvasElement (no
 * html2canvas) so panel clipping, art framing and bubble geometry are pixel
 * deterministic and match the editor exactly. From there we offer PNG (one
 * page or all pages) and a multi-page PDF (jsPDF, one page per manga page).
 *
 * Image loading note: panel `imageUrl` is a same-origin proxy
 * (`/api/media/...`) that 302-redirects to a presigned Wasabi URL on another
 * domain — drawing that straight into a canvas via `new Image()` taints it
 * (toBlob/toDataURL then throw). We instead `fetch()` the URL (which follows
 * the redirect) and turn the response into a `Blob` → local `Object URL`, so
 * by the time it hits `<img>`/`drawImage` there is no cross-origin resource
 * left to taint the canvas.
 */
import jsPDF from 'jspdf';
import type { MangaPage, MangaPanel, MangaBubble } from './api';
import { smoothClosedPath, centroidOf, rayPolygonExit, type Point } from './bubble-shape';
import { runPageFlip } from './page-flip';
import {
  generateBubblePath, thoughtTailCircles, toPathBubble,
  resolveFontFamily, resolveBubbleStyle, bubbleFontWeight, bubbleFontStyleCss, defaultBubbleFontPx, defaultBubblePaddingPx,
  fitBubbleFontPx, BUBBLE_LINE_HEIGHT, computeSfxLayout, groupJoinedBubbles, connectorPathsForGroup, computeTailOmissions,
} from './bubble-path';

/**
 * Shared geometry math for a bubble's body/tail/text-box in PAGE PIXELS — the
 * SAME orchestration `computeBubbleGeometry` in manga-editor.tsx uses (kept as
 * a sibling copy here rather than imported, since manga-editor.tsx imports
 * THIS module's export functions — importing the other way round would create
 * a circular module dependency). The underlying vector math itself is never
 * duplicated: both call the very same `generateBubblePath`/`toPathBubble`/
 * `thoughtTailCircles` from bubble-path.ts for the standard types, and the
 * same `smoothClosedPath`/`centroidOf`/`rayPolygonExit` from bubble-shape.ts
 * for point-drawn custom shapes.
 */
interface BubbleGeometryPx {
  boxX: number; boxY: number; boxW: number; boxH: number;
  pathD: string;
  thoughtCircles: { cx: number; cy: number; r: number }[];
  customTail: { p1: Point; tip: Point; p2: Point } | null;
  foX: number; foY: number; foW: number; foH: number;
  preferredFontPx: number;
}

function computeBubbleGeometryPx(b: MangaBubble, pageW: number, pageH: number, omitTail = false): BubbleGeometryPx {
  const boxX = b.x * pageW, boxY = b.y * pageH;
  const boxW = Math.max(1, b.w * pageW), boxH = Math.max(1, b.h * pageH);
  const cx = boxX + boxW / 2, cy = boxY + boxH / 2;
  const tailX = cx + (b.tail?.x ?? 0) * pageW;
  const tailY = cy + (b.tail?.y ?? 0) * pageH;
  const scale = pageW / 800;
  const hasCustomShape = !!(b.shape && b.shape.body.length >= 3);

  let pathD = '';
  let thoughtCircles: { cx: number; cy: number; r: number }[] = [];
  let customTail: { p1: Point; tip: Point; p2: Point } | null = null;

  if (b.type === 'sfx') {
    // No body at all — 'sfx' is giant text only, see `computeSfxLayout` and
    // the dedicated `drawSfxBubble`/`sfxSvgMarkup` renderers below.
  } else if (hasCustomShape) {
    const pts = b.shape!.body.map((p) => ({ x: boxX + p.x * boxW, y: boxY + p.y * boxH }));
    pathD = smoothClosedPath(pts);
    if (!omitTail) {
      const c = centroidOf(pts);
      const ang = Math.atan2(tailY - c.y, tailX - c.x);
      if (b.type === 'thought') {
        const p0 = rayPolygonExit(c, ang, pts);
        thoughtCircles = [0.55, 0.78, 1].map((t, i) => ({
          cx: p0.x + (tailX - p0.x) * t, cy: p0.y + (tailY - p0.y) * t, r: (7 - i * 2) * scale,
        }));
      } else if (b.type !== 'narration' && b.type !== 'caption' && b.type !== 'shout') {
        const a1 = rayPolygonExit(c, ang - 0.35, pts), a2 = rayPolygonExit(c, ang + 0.35, pts);
        customTail = { p1: a1, tip: { x: tailX, y: tailY }, p2: a2 };
      }
    }
  } else {
    const pb = toPathBubble(b, pageW, pageH, omitTail);
    pathD = generateBubblePath(pb);
    if (pb.type === 'thought') thoughtCircles = thoughtTailCircles(pb);
  }

  const padPx = b.padding ?? defaultBubblePaddingPx(b, pageW);
  const foX = boxX + padPx, foY = boxY + padPx;
  const foW = Math.max(4, boxW - 2 * padPx), foH = Math.max(4, boxH - 2 * padPx);

  const preferredFontPx = b.fontSize ?? defaultBubbleFontPx(b, pageW);
  return { boxX, boxY, boxW, boxH, pathD, thoughtCircles, customTail, foX, foY, foW, foH, preferredFontPx };
}

// ── Same-origin-safe image loading (see file header) ────────────────────────
interface LoadedImage { image: HTMLImageElement; width: number; height: number }

async function loadImage(url: string): Promise<LoadedImage> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Falha ao baixar imagem (HTTP ${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Falha ao decodificar imagem'));
      img.src = objectUrl;
    });
    return { image, width: image.naturalWidth || 1, height: image.naturalHeight || 1 };
  } finally {
    // Safe to revoke once `onload` fired — the bitmap is already decoded.
    URL.revokeObjectURL(objectUrl);
  }
}

/** Loads an image the same same-origin-safe way as `loadImage` (see file
 * header), but resolves to a `data:` URL instead of a decoded `<img>` — used
 * by the HTML export, which must be a fully offline, self-contained file. */
async function loadImageDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Falha ao baixar imagem (HTTP ${res.status})`);
  const blob = await res.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao ler a imagem.'));
    reader.readAsDataURL(blob);
  });
}

/** Escapes text for safe interpolation into both HTML text nodes and
 * double-quoted HTML attributes — bubble text / titles are arbitrary user input. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** `object-fit: cover` math: crop rect (source space) that fills `dw x dh` without distortion. */
function coverFit(iw: number, ih: number, dw: number, dh: number): { sx: number; sy: number; sw: number; sh: number } {
  const scale = Math.max(dw / iw, dh / ih);
  const sw = dw / scale, sh = dh / scale;
  return { sx: (iw - sw) / 2, sy: (ih - sh) / 2, sw, sh };
}

/** Loads the fonts used by bubble text before we measure/draw anything (Google
 * Fonts are declared in index.html but may not be ready on a fresh export). */
async function ensureFontsReady(): Promise<void> {
  try {
    await Promise.all([
      document.fonts.load('700 32px "Comic Neue"'),
      document.fonts.load('400 32px "Bangers"'),
      document.fonts.load('700 32px "Fraunces"'),
    ]);
    await document.fonts.ready;
  } catch {
    // Best effort — proceed with fallback fonts rather than blocking export.
  }
}

// Canvas 2D's `letterSpacing` is a real (if not universally shipped) part of
// the spec — set it only where supported, and always inside a save/restore
// so it never leaks onto the next bubble's text.
function trySetLetterSpacing(ctx: CanvasRenderingContext2D, value: string): void {
  if ('letterSpacing' in ctx) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = value;
}

// ── Word wrap (manual measureText, mirrors CSS overflow-wrap: break-word) ──
function breakLongWord(ctx: CanvasRenderingContext2D, word: string, maxWidth: number): string[] {
  const out: string[] = [];
  let chunk = '';
  for (const ch of word) {
    const candidate = chunk + ch;
    if (chunk && ctx.measureText(candidate).width > maxWidth) { out.push(chunk); chunk = ch; }
    else chunk = candidate;
  }
  if (chunk) out.push(chunk);
  return out;
}

function wrapParagraph(ctx: CanvasRenderingContext2D, paragraph: string, maxWidth: number): string[] {
  const words = paragraph.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) { line = candidate; continue; }
    if (line) { lines.push(line); line = ''; }
    if (ctx.measureText(word).width <= maxWidth) { line = word; continue; }
    const chunks = breakLongWord(ctx, word, maxWidth);
    for (let i = 0; i < chunks.length - 1; i++) lines.push(chunks[i]);
    line = chunks[chunks.length - 1] ?? '';
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  return text.split('\n').flatMap((p) => wrapParagraph(ctx, p, maxWidth));
}

// ── Panel rendering ──────────────────────────────────────────────────────────
async function drawPanel(
  ctx: CanvasRenderingContext2D,
  panel: MangaPanel,
  pageW: number,
  pageH: number,
  imageCache: Map<string, Promise<LoadedImage>>,
): Promise<void> {
  if (panel.points.length < 3) return;
  const xs = panel.points.map((p) => p.x), ys = panel.points.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const bx = minX * pageW, by = minY * pageH;
  const bw = Math.max(1, (maxX - minX) * pageW), bh = Math.max(1, (maxY - minY) * pageH);

  const clipPath = new Path2D();
  panel.points.forEach((p, i) => {
    const x = p.x * pageW, y = p.y * pageH;
    if (i === 0) clipPath.moveTo(x, y); else clipPath.lineTo(x, y);
  });
  clipPath.closePath();

  ctx.save();
  ctx.clip(clipPath);
  if (panel.imageUrl) {
    try {
      let pending = imageCache.get(panel.imageUrl);
      if (!pending) { pending = loadImage(panel.imageUrl); imageCache.set(panel.imageUrl, pending); }
      const { image, width: iw, height: ih } = await pending;
      const cover = coverFit(iw, ih, bw, bh);
      const cx = bx + bw / 2, cy = by + bh / 2;
      const scale = panel.scale ?? 1;
      const sx = scale * (panel.flipX ? -1 : 1);
      const sy = scale * (panel.flipY ? -1 : 1);
      const tx = (panel.offsetX ?? 0) * bw;
      const ty = (panel.offsetY ?? 0) * bh;
      // Reproduces `transform: translate(offsetX%, offsetY%) scale(sx, sy)`
      // with `transform-origin: center` around the box's own center (cx, cy):
      // p' = center*(1 - s) + t + s*p — see file header for the derivation.
      ctx.translate(cx * (1 - sx) + tx, cy * (1 - sy) + ty);
      ctx.scale(sx, sy);
      ctx.drawImage(image, cover.sx, cover.sy, cover.sw, cover.sh, bx, by, bw, bh);
    } catch (err) {
      console.error(`[manga-export] falha ao carregar a arte do quadro ${panel.id}:`, err);
      ctx.fillStyle = '#f3f0e8';
      ctx.fillRect(bx, by, bw, bh);
    }
  } else {
    ctx.fillStyle = '#f3f0e8';
    ctx.fillRect(bx, by, bw, bh);
  }
  ctx.restore();

  // Crisp panel border (~3px @ a 1000px-wide reference page).
  ctx.save();
  ctx.strokeStyle = '#111';
  ctx.lineWidth = Math.max(1, (pageW / 1000) * 3);
  ctx.lineJoin = 'round';
  ctx.stroke(clipPath);
  ctx.restore();
}

// ── Bubble rendering ─────────────────────────────────────────────────────────
// Body/tail/thought-circles are drawn directly in real PAGE PIXELS — the exact
// same coordinate space `generateBubblePath`/`toPathBubble` already produce —
// no per-bubble translate/scale trick needed, unlike the old stretched 0..100
// box. Per-bubble style (fill/stroke/strokeWidth/whisper dasharray) mirrors
// `resolveBubbleStyle` exactly.
function drawBubbleShape(ctx: CanvasRenderingContext2D, b: MangaBubble, g: BubbleGeometryPx): void {
  const st = resolveBubbleStyle(b);
  ctx.save();
  ctx.fillStyle = st.fill;
  ctx.strokeStyle = st.stroke;
  ctx.lineWidth = st.strokeWidth;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (b.type === 'whisper') ctx.setLineDash([6, 6]);

  const path = new Path2D(g.pathD);
  ctx.fill(path);
  ctx.stroke(path);
  ctx.setLineDash([]); // never let the dash leak onto the tail/circles below

  if (g.customTail) {
    const { p1, tip, p2 } = g.customTail;
    // fill hides the body's edge under the tail, then stroke only the two sides
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(tip.x, tip.y); ctx.lineTo(p2.x, p2.y); ctx.closePath();
    ctx.fill();
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(tip.x, tip.y); ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
  }
  for (const c of g.thoughtCircles) {
    ctx.beginPath(); ctx.arc(c.cx, c.cy, Math.max(3, c.r), 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
  }
  ctx.restore();
}

/**
 * Fill-only re-draw of the exact same body/tail path — used as the SECOND
 * pass of the "balloon chain" fusion trick (see `groupJoinedBubbles`): draw
 * every member of a fused group once with stroke (pass 1), then again fill
 * -only on top (pass 2) — the second pass paints over each other's internal
 * stroke wherever the shapes overlap, leaving only the outer silhouette
 * stroked. No-op for thought circles/tail outline stroke (fill only).
 */
function drawBubbleShapeFillOnly(ctx: CanvasRenderingContext2D, b: MangaBubble, g: BubbleGeometryPx): void {
  const st = resolveBubbleStyle(b);
  ctx.save();
  ctx.fillStyle = st.fill;
  ctx.fill(new Path2D(g.pathD));
  if (g.customTail) {
    const { p1, tip, p2 } = g.customTail;
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(tip.x, tip.y); ctx.lineTo(p2.x, p2.y); ctx.closePath();
    ctx.fill();
  }
  for (const c of g.thoughtCircles) {
    ctx.beginPath(); ctx.arc(c.cx, c.cy, Math.max(3, c.r), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Draws an 'sfx' bubble — giant display text, NO body/tail — using the same
 * geometry/typography `computeSfxLayout` gives every renderer. `strokeText`
 * BEFORE `fillText` (paint-order: stroke) with a round line join gives the
 * classic thick comic-lettering outline.
 */
function drawSfxBubble(ctx: CanvasRenderingContext2D, b: MangaBubble, pageW: number, pageH: number): void {
  const layout = computeSfxLayout(b, pageW, pageH);
  ctx.save();
  ctx.translate(layout.cx, layout.cy);
  ctx.rotate((layout.rotation * Math.PI) / 180);
  ctx.font = `400 ${layout.fontSizePx}px ${layout.fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  ctx.lineWidth = layout.strokeWidth;
  ctx.strokeStyle = layout.stroke;
  ctx.fillStyle = layout.fill;
  const lineHeightPx = layout.fontSizePx * 0.95;
  const totalH = lineHeightPx * layout.lines.length;
  const startY = -totalH / 2 + lineHeightPx / 2;
  layout.lines.forEach((line, i) => {
    const y = startY + i * lineHeightPx;
    ctx.strokeText(line, 0, y);
    ctx.fillText(line, 0, y);
  });
  ctx.restore();
}

function drawBubbleText(ctx: CanvasRenderingContext2D, b: MangaBubble, g: BubbleGeometryPx): void {
  const text = (b.type === 'shout' ? b.text.toUpperCase() : b.text) || '';
  if (!text.trim()) return;
  const st = resolveBubbleStyle(b);
  const { foX: contentX, foY: contentY, foW: contentW, foH: contentH } = g;

  const fontFamily = resolveFontFamily(b.type, b.fontFamily);
  const weight = bubbleFontWeight(b);
  const italic = bubbleFontStyleCss(b) === 'italic';
  // Same fit-to-box shrink as the editor/reader: `preferredPx` (explicit
  // b.fontSize, or the default fallback) is a CEILING, never a target — the
  // manual canvas wrap below still owns line-breaking, only the SIZE comes
  // from the DOM-measured fit, so text never gets clipped on export either.
  const preferredPx = g.preferredFontPx;
  const fontPx = fitBubbleFontPx(text, contentW, contentH, fontFamily, String(weight), italic ? 'italic' : 'normal', preferredPx);
  const lineHeightPx = fontPx * BUBBLE_LINE_HEIGHT; // matches the editor's foreignObject lineHeight

  ctx.save();
  ctx.beginPath();
  ctx.rect(contentX, contentY, contentW, contentH); // mirrors the text layer's `overflow: hidden`
  ctx.clip();
  ctx.font = `${italic ? 'italic ' : ''}${weight} ${fontPx}px ${fontFamily}`;
  ctx.fillStyle = st.textColor;
  ctx.textBaseline = 'middle';
  if (b.type === 'shout') trySetLetterSpacing(ctx, '0.02em');

  const lines = wrapText(ctx, text, contentW);
  const totalH = lines.length * lineHeightPx;
  const startY = contentY + contentH / 2 - totalH / 2 + lineHeightPx / 2;

  let x: number;
  if (b.align === 'left') { ctx.textAlign = 'left'; x = contentX; }
  else if (b.align === 'right') { ctx.textAlign = 'right'; x = contentX + contentW; }
  else { ctx.textAlign = 'center'; x = contentX + contentW / 2; }

  lines.forEach((line, i) => ctx.fillText(line, x, startY + i * lineHeightPx));
  ctx.restore();
}

/** Runs `draw` inside a `ctx.translate/rotate/translate` sandwich around
 * `b`'s OWN box center (a no-op, no save/restore, when `b.rotation` is unset)
 * — the canvas twin of every other renderer's `<g transform="rotate(...)">`:
 * rotates the WHOLE bubble (body + tail + text, whatever `draw` paints) as
 * one rigid group. */
function withBubbleRotation(ctx: CanvasRenderingContext2D, b: MangaBubble, g: BubbleGeometryPx, draw: () => void): void {
  const rotation = b.rotation ?? 0;
  if (!rotation) { draw(); return; }
  const cx = g.boxX + g.boxW / 2, cy = g.boxY + g.boxH / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-cx, -cy);
  draw();
  ctx.restore();
}

function drawBubble(ctx: CanvasRenderingContext2D, b: MangaBubble, pageW: number, pageH: number): void {
  if (b.type === 'sfx') { drawSfxBubble(ctx, b, pageW, pageH); return; } // rotates internally, see drawSfxBubble
  const g = computeBubbleGeometryPx(b, pageW, pageH);
  withBubbleRotation(ctx, b, g, () => {
    drawBubbleShape(ctx, b, g);
    drawBubbleText(ctx, b, g);
  });
}

/** Fills (and, unless `fillOnly`, strokes) every connector "neck" path
 * between two CLOSE-but-not-overlapping members of a fused/bridged group
 * (see `connectorPathsForGroup`) — the canvas twin of `bubbleShapeSvg`'s
 * bridge handling. Drawn in UNROTATED page space: bridging assumes its two
 * flanking bubbles are axis-aligned, the overwhelmingly common case — a
 * bridged bubble that's ALSO rotated is an accepted edge case (same
 * "contained scope" tradeoff as the resize handles staying axis-aligned). */
function drawConnectors(
  ctx: CanvasRenderingContext2D,
  paths: string[],
  style: { fill: string; stroke: string; strokeWidth: number },
  fillOnly: boolean,
): void {
  for (const d of paths) {
    const path = new Path2D(d);
    ctx.save();
    ctx.fillStyle = style.fill;
    ctx.fill(path);
    if (!fillOnly) {
      ctx.strokeStyle = style.stroke;
      ctx.lineWidth = style.strokeWidth;
      ctx.lineJoin = 'round';
      ctx.stroke(path);
    }
    ctx.restore();
  }
}

/**
 * Draws every bubble on the page, grouping "balloon chain" fusions/bridges
 * (see `groupJoinedBubbles`) so overlapping same-type/same-fill balloons
 * merge into one seamless blob (or, merely CLOSE ones, connect with a bridge
 * — see `connectorPathsForGroup`): pass 1 (stroke+fill) for every member +
 * bridges stroked, pass 2 (fill-only) for every member + bridges again on top
 * to erase the internal seams, THEN every member's text. Singleton groups
 * take the original single-pass `drawBubble` fast path (identical output,
 * fewer draw calls).
 */
function drawBubbles(ctx: CanvasRenderingContext2D, bubbles: MangaBubble[], pageW: number, pageH: number): void {
  const groups = groupJoinedBubbles(bubbles, pageW, pageH);
  // "Only ONE visible tail per connected group" (see `computeTailOmissions`)
  // — same rule every renderer applies.
  const tailOmitIds = computeTailOmissions(bubbles, groups);
  for (const group of groups) {
    const members = group.map((i) => bubbles[i]);
    if (members.length === 1) { drawBubble(ctx, members[0], pageW, pageH); continue; }
    const geoms = members.map((b) => computeBubbleGeometryPx(b, pageW, pageH, tailOmitIds.has(b.id)));
    const connectors = connectorPathsForGroup(members, pageW, pageH);
    const connectorStyle = resolveBubbleStyle(members[0]); // every member shares type/effective fill
    members.forEach((b, i) => withBubbleRotation(ctx, b, geoms[i], () => drawBubbleShape(ctx, b, geoms[i])));
    drawConnectors(ctx, connectors, connectorStyle, false);
    members.forEach((b, i) => withBubbleRotation(ctx, b, geoms[i], () => drawBubbleShapeFillOnly(ctx, b, geoms[i])));
    drawConnectors(ctx, connectors, connectorStyle, true);
    members.forEach((b, i) => withBubbleRotation(ctx, b, geoms[i], () => drawBubbleText(ctx, b, geoms[i])));
  }
}

// ── SVG bubble/panel markup (HTML export) ───────────────────────────────────
// Mirrors `drawBubbleShape` 1:1, but emits real SVG markup (path/polygon/
// circle strings) instead of Canvas 2D draw calls, consuming the exact same
// `BubbleGeometryPx` — so the two renderers can never visually drift apart.
function bubbleShapeSvg(b: MangaBubble, g: BubbleGeometryPx): string {
  const st = resolveBubbleStyle(b);
  const dash = b.type === 'whisper' ? ' stroke-dasharray="6 6"' : '';
  let out = `<path d="${g.pathD}" fill="${st.fill}" stroke="${st.stroke}" stroke-width="${st.strokeWidth}" stroke-linejoin="round" stroke-linecap="round"${dash}/>`;
  if (g.customTail) {
    const { p1, tip, p2 } = g.customTail;
    // fill hides the body's edge under the tail, then stroke only the two sides
    out += `<polygon points="${p1.x},${p1.y} ${tip.x},${tip.y} ${p2.x},${p2.y}" fill="${st.fill}"/>`;
    out += `<path d="M ${p1.x} ${p1.y} L ${tip.x} ${tip.y} L ${p2.x} ${p2.y}" fill="none" stroke="${st.stroke}" stroke-width="${st.strokeWidth}" stroke-linejoin="round" stroke-linecap="round"/>`;
  }
  for (const c of g.thoughtCircles) {
    out += `<circle cx="${c.cx}" cy="${c.cy}" r="${Math.max(3, c.r)}" fill="${st.fill}" stroke="${st.stroke}" stroke-width="${st.strokeWidth}"/>`;
  }
  return out;
}

/** Fill-only twin of `bubbleShapeSvg` — same shapes, no stroke — the SVG
 * counterpart of `drawBubbleShapeFillOnly` (pass 2 of the fusion trick). */
function bubbleShapeFillOnlySvg(b: MangaBubble, g: BubbleGeometryPx): string {
  const st = resolveBubbleStyle(b);
  let out = `<path d="${g.pathD}" fill="${st.fill}" stroke="none"/>`;
  if (g.customTail) {
    const { p1, tip, p2 } = g.customTail;
    out += `<polygon points="${p1.x},${p1.y} ${tip.x},${tip.y} ${p2.x},${p2.y}" fill="${st.fill}"/>`;
  }
  for (const c of g.thoughtCircles) {
    out += `<circle cx="${c.cx}" cy="${c.cy}" r="${Math.max(3, c.r)}" fill="${st.fill}"/>`;
  }
  return out;
}

/** The `<foreignObject>` text layer markup shared by `bubbleSvgMarkup` (single
 * bubble, fast path) and `bubbleTextOnlySvg` (fused-group path) — identical
 * output, just factored out so the two can never drift. */
function bubbleTextForeignObject(b: MangaBubble, g: BubbleGeometryPx): string {
  const st = resolveBubbleStyle(b);
  const text = (b.type === 'shout' ? b.text.toUpperCase() : b.text) || '';
  const fontFamily = resolveFontFamily(b.type, b.fontFamily);
  const weight = bubbleFontWeight(b);
  const italic = bubbleFontStyleCss(b) === 'italic';
  // Fitted AT EXPORT TIME (DOM is available — this runs in-browser), same
  // ceiling/shrink rule as every other renderer: never crop, never suppress.
  const preferredPx = g.preferredFontPx;
  const fontPx = fitBubbleFontPx(text, g.foW, g.foH, fontFamily, String(weight), italic ? 'italic' : 'normal', preferredPx);
  const justify = b.align === 'left' ? 'flex-start' : b.align === 'right' ? 'flex-end' : 'center';
  const textAlign = b.align ?? 'center';
  const divStyle = [
    'display:flex', 'align-items:center', `justify-content:${justify}`, `text-align:${textAlign}`,
    'width:100%', 'height:100%', 'overflow:hidden',
    `font-family:${fontFamily}`, `color:${st.textColor}`, `font-weight:${weight}`,
    `font-style:${italic ? 'italic' : 'normal'}`,
    `text-transform:${b.type === 'shout' ? 'uppercase' : 'none'}`,
    `letter-spacing:${b.type === 'shout' ? '0.02em' : '0'}`,
    `font-size:${fontPx.toFixed(2)}px`, `line-height:${BUBBLE_LINE_HEIGHT}`, 'word-break:break-word',
  ].join(';');
  // The full text lives in `data-text` (an HTML attribute, so entities are
  // decoded back to the real string by `getAttribute` — no unescaping
  // needed); the div itself starts EMPTY — `readerRuntime`'s entrance queue
  // sets `textContent` to the FULL text in one shot (fade-in only, no
  // progressive/typewriter reveal — see `replayBubbles` there).
  return `<foreignObject x="${g.foX.toFixed(2)}" y="${g.foY.toFixed(2)}" width="${g.foW.toFixed(2)}" height="${g.foH.toFixed(2)}">`
    + `<div xmlns="http://www.w3.org/1999/xhtml" class="bubble-text" data-text="${escapeHtml(text)}" style="${divStyle}"></div>`
    + `</foreignObject>`;
}

/**
 * 'sfx' bubble as SVG — giant display text, no body/tail. The rotation lives
 * on an INNER `<g transform="rotate(...)">` (SVG presentation attribute, not
 * CSS) so it never fights the OUTER `.bubble` group's CSS `anim-pop` entrance
 * animation (which animates a CSS `transform: scale()` on that outer group —
 * mixing a static CSS transform in there would get clobbered by the keyframe).
 * `paint-order:stroke` + round joins give the thick comic-lettering outline.
 */
function sfxSvgMarkup(b: MangaBubble, pageW: number, pageH: number, panelIdx: number): string {
  const layout = computeSfxLayout(b, pageW, pageH);
  const lineHeightPx = layout.fontSizePx * 0.95;
  const totalH = lineHeightPx * layout.lines.length;
  const startY = layout.cy - totalH / 2 + lineHeightPx / 2;
  const textEls = layout.lines.map((line, i) => {
    const y = startY + i * lineHeightPx;
    return `<text x="${layout.cx.toFixed(2)}" y="${y.toFixed(2)}" text-anchor="middle" dominant-baseline="middle" `
      + `font-family="${layout.fontFamily}" font-size="${layout.fontSizePx.toFixed(2)}" `
      + `fill="${layout.fill}" stroke="${layout.stroke}" stroke-width="${layout.strokeWidth}" `
      + `style="paint-order:stroke;stroke-linejoin:round">${escapeHtml(line)}</text>`;
  }).join('');
  return `<g class="bubble" data-panel="${panelIdx}" style="transform-origin:${layout.cx.toFixed(2)}px ${layout.cy.toFixed(2)}px">`
    + `<g transform="rotate(${layout.rotation} ${layout.cx.toFixed(2)} ${layout.cy.toFixed(2)})">${textEls}</g>`
    + `</g>`;
}

/**
 * One bubble as SVG: body/tail/thought-circles are already in real PAGE
 * PIXELS (from `generateBubblePath`/`toPathBubble`, or the custom-shape ray
 * casting), so no scaled wrapper `<g>` is needed anymore — the TEXT is a
 * `<foreignObject>` sized/positioned from the SAME geometry's padded content
 * box (`foX/foY/foW/foH`), matching the editor's `BubbleNode` pixel-for-pixel.
 * `data-panel` (bubble → owning panel index, or -1) drives the reader's
 * per-panel "pop in" animation grouping. Singleton (non-fused) fast path —
 * fused groups use `bubbleShapeOnlySvg` + `bubbleTextOnlySvg` instead (see
 * `buildPageMarkup`) so the fill-only fusion patch can sit BETWEEN the shapes
 * and the text of every member.
 */
function bubbleSvgMarkup(b: MangaBubble, pageW: number, pageH: number, panelIdx: number): string {
  if (b.type === 'sfx') return sfxSvgMarkup(b, pageW, pageH, panelIdx);
  const g = computeBubbleGeometryPx(b, pageW, pageH);
  const originX = g.boxX + g.boxW / 2, originY = g.boxY + g.boxH / 2;
  // Rotation on its OWN inner <g> (SVG presentation attribute) — same split
  // as `sfxSvgMarkup`: never mixed with the outer `.bubble` group's CSS
  // `anim-pop` entrance animation. Wraps shape AND text together so the
  // whole bubble spins as one rigid unit.
  const rotation = b.rotation ?? 0;
  const rotAttr = rotation ? ` transform="rotate(${rotation} ${originX.toFixed(2)} ${originY.toFixed(2)})"` : '';
  return `<g class="bubble" data-panel="${panelIdx}" style="transform-origin:${originX.toFixed(2)}px ${originY.toFixed(2)}px">`
    + `<g${rotAttr}>` + bubbleShapeSvg(b, g) + bubbleTextForeignObject(b, g) + `</g>`
    + `</g>`;
}

/** Shape-only half of a FUSED bubble (see `groupJoinedBubbles`) — `fillOnly`
 * picks pass 1 (full stroke+fill, `class="bubble"` so the reader's entrance
 * animation still finds/pops it) or pass 2 (fill-only fusion patch, inert
 * `class="bubble-fuse"`, no animation hook, no `data-panel`). Rotation lives
 * on its own inner `<g>`, same split as `bubbleSvgMarkup`. */
function bubbleShapeOnlySvg(b: MangaBubble, g: BubbleGeometryPx, panelIdx: number, fillOnly: boolean): string {
  const inner = fillOnly ? bubbleShapeFillOnlySvg(b, g) : bubbleShapeSvg(b, g);
  const originX = g.boxX + g.boxW / 2, originY = g.boxY + g.boxH / 2;
  const rotation = b.rotation ?? 0;
  const rotAttr = rotation ? ` transform="rotate(${rotation} ${originX.toFixed(2)} ${originY.toFixed(2)})"` : '';
  const wrapped = `<g${rotAttr}>${inner}</g>`;
  if (fillOnly) return `<g class="bubble-fuse" data-panel="${panelIdx}" style="transform-origin:${originX.toFixed(2)}px ${originY.toFixed(2)}px">${wrapped}</g>`;
  return `<g class="bubble" data-panel="${panelIdx}" style="transform-origin:${originX.toFixed(2)}px ${originY.toFixed(2)}px">${wrapped}</g>`;
}

/** Text-only half of a FUSED bubble's markup — pairs with `bubbleShapeOnlySvg`. */
function bubbleTextOnlySvg(b: MangaBubble, g: BubbleGeometryPx, panelIdx: number): string {
  const originX = g.boxX + g.boxW / 2, originY = g.boxY + g.boxH / 2;
  const rotation = b.rotation ?? 0;
  const rotAttr = rotation ? ` transform="rotate(${rotation} ${originX.toFixed(2)} ${originY.toFixed(2)})"` : '';
  return `<g class="bubble" data-panel="${panelIdx}" style="transform-origin:${originX.toFixed(2)}px ${originY.toFixed(2)}px">`
    + `<g${rotAttr}>` + bubbleTextForeignObject(b, g) + `</g>`
    + `</g>`;
}

/** SVG markup for a connector "neck" bridging two CLOSE-but-not-overlapping
 * members of a group (see `connectorPathsForGroup`) — `class="bubble-fuse"`
 * so the reader's guided-view re-parenting (`replayBubbles` in
 * `readerRuntime`) picks it up together with the group's own fill patches
 * and moves it above/below the veil in lockstep. `panelIdx` uses the GROUP's
 * first member as a representative — proximity-bridged bubbles are, in
 * practice, always inside the same panel. */
function connectorSvg(d: string, style: { fill: string; stroke: string; strokeWidth: number }, panelIdx: number, fillOnly: boolean): string {
  const inner = fillOnly
    ? `<path d="${d}" fill="${style.fill}" stroke="none"/>`
    : `<path d="${d}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}" stroke-linejoin="round"/>`;
  return `<g class="bubble-fuse" data-panel="${panelIdx}">${inner}</g>`;
}

/**
 * One manga page as a self-contained `<div class="page-wrap"><svg>…</svg></div>`:
 * panels first (clip polygon + framed `<image>` + border), bubbles on top.
 * Panel art uses `preserveAspectRatio="xMidYMid slice"` — SVG's native
 * `object-fit: cover` equivalent — so no manual crop-rect math is needed; the
 * pan/zoom/flip transform around the panel box's own center reproduces
 * `drawPanel`'s `p' = center*(1-s) + t + s*p` exactly (see that function's
 * comment), just spelled out as `translate(t) translate(c) scale(s) translate(-c)`.
 */
async function buildPageMarkup(
  page: MangaPage,
  pageIndex: number,
  imageCache: Map<string, Promise<string>>,
): Promise<{ html: string; panelsData: { points: Point[] }[]; width: number; height: number }> {
  const W = page.width || 1000, H = page.height || 1414;
  const panels = page.layout.panels ?? [];
  const bubbles = page.layout.bubbles ?? [];

  let defs = '';
  let panelsMarkup = '';
  const panelBboxes: { minX: number; maxX: number; minY: number; maxY: number }[] = [];

  for (let i = 0; i < panels.length; i++) {
    const panel = panels[i];
    if (panel.points.length < 3) { panelBboxes.push({ minX: 0, maxX: 0, minY: 0, maxY: 0 }); continue; }
    const xs = panel.points.map((p) => p.x), ys = panel.points.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    panelBboxes.push({ minX, maxX, minY, maxY });
    const bx = minX * W, by = minY * H;
    const bw = Math.max(1, (maxX - minX) * W), bh = Math.max(1, (maxY - minY) * H);
    const ptsAttr = panel.points.map((p) => `${(p.x * W).toFixed(2)},${(p.y * H).toFixed(2)}`).join(' ');
    const clipId = `clip-${pageIndex}-${i}`;
    defs += `<clipPath id="${clipId}"><polygon points="${ptsAttr}"/></clipPath>`;

    let imageMarkup: string;
    if (panel.imageUrl) {
      try {
        let pending = imageCache.get(panel.imageUrl);
        if (!pending) { pending = loadImageDataUrl(panel.imageUrl); imageCache.set(panel.imageUrl, pending); }
        const dataUrl = await pending;
        const scale = panel.scale ?? 1;
        const sx = scale * (panel.flipX ? -1 : 1);
        const sy = scale * (panel.flipY ? -1 : 1);
        const tx = (panel.offsetX ?? 0) * bw, ty = (panel.offsetY ?? 0) * bh;
        const cx = bx + bw / 2, cy = by + bh / 2;
        const transform = `translate(${tx.toFixed(2)},${ty.toFixed(2)}) translate(${cx.toFixed(2)},${cy.toFixed(2)}) scale(${sx.toFixed(4)},${sy.toFixed(4)}) translate(${(-cx).toFixed(2)},${(-cy).toFixed(2)})`;
        imageMarkup = `<g transform="${transform}"><image href="${dataUrl}" x="${bx.toFixed(2)}" y="${by.toFixed(2)}" width="${bw.toFixed(2)}" height="${bh.toFixed(2)}" preserveAspectRatio="xMidYMid slice"/></g>`;
      } catch (err) {
        console.error(`[manga-export] falha ao carregar a arte do quadro ${panel.id}:`, err);
        imageMarkup = `<rect x="${bx.toFixed(2)}" y="${by.toFixed(2)}" width="${bw.toFixed(2)}" height="${bh.toFixed(2)}" fill="#f3f0e8"/>`;
      }
    } else {
      imageMarkup = `<rect x="${bx.toFixed(2)}" y="${by.toFixed(2)}" width="${bw.toFixed(2)}" height="${bh.toFixed(2)}" fill="#f3f0e8"/>`;
    }

    const sw = Math.max(1, (W / 1000) * 3);
    panelsMarkup += `<g clip-path="url(#${clipId})">${imageMarkup}</g><polygon points="${ptsAttr}" fill="none" stroke="#111" stroke-width="${sw.toFixed(2)}"/>`;
  }

  // Bubble → owning panel: same bbox-center test as the reader's `inFocus`.
  const panelIdxFor = (b: MangaBubble): number => {
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
    for (let i = 0; i < panelBboxes.length; i++) {
      const box = panelBboxes[i];
      if (cx >= box.minX && cx <= box.maxX && cy >= box.minY && cy <= box.maxY) return i;
    }
    return -1;
  };
  // Fused/bridged "balloon chain" groups (see `groupJoinedBubbles`):
  // singletons take the self-contained `bubbleSvgMarkup` fast path;
  // multi-member groups split into shape+bridge passes (1: stroked, 2:
  // fill-only patch) THEN every member's text, so the patch lands strictly
  // between shapes and text.
  let bubblesMarkup = '';
  const bubbleGroups = groupJoinedBubbles(bubbles, W, H);
  // "Only ONE visible tail per connected group" (see `computeTailOmissions`)
  // — same rule every renderer applies.
  const tailOmitIds = computeTailOmissions(bubbles, bubbleGroups);
  for (const group of bubbleGroups) {
    const members = group.map((i) => bubbles[i]);
    if (members.length === 1) { bubblesMarkup += bubbleSvgMarkup(members[0], W, H, panelIdxFor(members[0])); continue; }
    const panelIdxs = members.map(panelIdxFor);
    const geoms = members.map((b) => computeBubbleGeometryPx(b, W, H, tailOmitIds.has(b.id)));
    const connectors = connectorPathsForGroup(members, W, H);
    const connectorStyle = resolveBubbleStyle(members[0]);
    const connectorPanelIdx = panelIdxs[0]; // proximity-bridged members are, in practice, always in the same panel
    members.forEach((b, i) => { bubblesMarkup += bubbleShapeOnlySvg(b, geoms[i], panelIdxs[i], false); });
    connectors.forEach((d) => { bubblesMarkup += connectorSvg(d, connectorStyle, connectorPanelIdx, false); });
    members.forEach((b, i) => { bubblesMarkup += bubbleShapeOnlySvg(b, geoms[i], panelIdxs[i], true); });
    connectors.forEach((d) => { bubblesMarkup += connectorSvg(d, connectorStyle, connectorPanelIdx, true); });
    members.forEach((b, i) => { bubblesMarkup += bubbleTextOnlySvg(b, geoms[i], panelIdxs[i]); });
  }

  // Stable original order for the guided reader's re-parenting (bubbles of
  // the focused panel are moved above the veil and back — data-oidx restores
  // the exact original stacking).
  let oidx = 0;
  bubblesMarkup = bubblesMarkup.replace(/<g class="(bubble|bubble-fuse)" /g, (m) => m + 'data-oidx="' + (oidx++) + '" ');

  const svg = `<svg id="page-svg-${pageIndex}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">`
    + `<defs>${defs}</defs><rect width="${W}" height="${H}" fill="#fff"/>${panelsMarkup}${bubblesMarkup}`
    + `<g class="veil-layer"><path id="veil-fill-${pageIndex}" class="veil-fill" fill-rule="evenodd"></path>`
    + `<path id="veil-outline-${pageIndex}" class="veil-outline"></path></g></svg>`;
  const html = `<div class="page-wrap" id="page-wrap-${pageIndex}" style="aspect-ratio:${W} / ${H};display:${pageIndex === 0 ? '' : 'none'}">${svg}</div>`;

  return { html, panelsData: panels.map((p) => ({ points: p.points })), width: W, height: H };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Renders one manga page (panels + bubbles, in that order) onto a fresh
 * canvas, at 2x the page's declared size for crisp export.
 */
export async function renderMangaPage(page: MangaPage): Promise<HTMLCanvasElement> {
  const pageW = page.width || 1000;
  const pageH = page.height || 1414;
  const EXPORT_SCALE = 2;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(pageW * EXPORT_SCALE);
  canvas.height = Math.round(pageH * EXPORT_SCALE);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D não é suportado neste navegador.');
  ctx.scale(EXPORT_SCALE, EXPORT_SCALE);

  await ensureFontsReady();

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, pageW, pageH);

  const imageCache = new Map<string, Promise<LoadedImage>>();
  for (const panel of page.layout.panels) {
    await drawPanel(ctx, panel, pageW, pageH, imageCache);
  }
  drawBubbles(ctx, page.layout.bubbles, pageW, pageH);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, type = 'image/png', quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Falha ao gerar a imagem do canvas.'))), type, quality);
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Exports a single page as a PNG file and triggers the browser download. */
export async function exportPagePng(page: MangaPage, filename = 'pagina.png'): Promise<void> {
  try {
    const canvas = await renderMangaPage(page);
    const blob = await canvasToBlob(canvas, 'image/png');
    downloadBlob(blob, filename.toLowerCase().endsWith('.png') ? filename : `${filename}.png`);
  } catch (err) {
    throw new Error(err instanceof Error ? `Falha ao exportar PNG: ${err.message}` : 'Falha ao exportar PNG.');
  }
}

/** Exports every page as individual PNG files (sequential, one download each). */
export async function exportAllPngs(
  pages: MangaPage[],
  baseName = 'pagina',
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  if (pages.length === 0) throw new Error('Não há páginas para exportar.');
  try {
    for (let i = 0; i < pages.length; i++) {
      const canvas = await renderMangaPage(pages[i]);
      const blob = await canvasToBlob(canvas, 'image/png');
      const num = String(i + 1).padStart(2, '0');
      downloadBlob(blob, `${baseName}-${num}.png`);
      onProgress?.(i + 1, pages.length);
      // A small gap between sequential downloads — browsers can silently drop
      // rapid-fire `<a download>` clicks otherwise.
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  } catch (err) {
    throw new Error(err instanceof Error ? `Falha ao exportar PNGs: ${err.message}` : 'Falha ao exportar PNGs.');
  }
}

/** Exports the whole manga as a single multi-page PDF (one page per manga page, own size each). */
export async function exportPdf(
  pages: MangaPage[],
  title = 'manga',
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  if (pages.length === 0) throw new Error('Não há páginas para exportar.');
  try {
    let pdf: jsPDF | null = null;
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const w = page.width || 1000, h = page.height || 1414;
      const canvas = await renderMangaPage(page);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const orientation = w >= h ? 'landscape' : 'portrait';
      if (!pdf) {
        pdf = new jsPDF({ orientation, unit: 'px', format: [w, h] });
      } else {
        pdf.addPage([w, h], orientation);
      }
      pdf.addImage(dataUrl, 'JPEG', 0, 0, w, h);
      onProgress?.(i + 1, pages.length);
    }
    if (!pdf) throw new Error('Nenhuma página processada.');
    pdf.save(title.toLowerCase().endsWith('.pdf') ? title : `${title}.pdf`);
  } catch (err) {
    throw new Error(err instanceof Error ? `Falha ao exportar PDF: ${err.message}` : 'Falha ao exportar PDF.');
  }
}

// ── HTML (interactive/animated) export ──────────────────────────────────────
// A single, offline, self-contained .html file that IS a manga reader: every
// page is real vector SVG (panel clip + framed art + the same bubble-shape
// paths the editor draws), with a tiny embedded reader mirroring the
// in-editor preview (guided quadro-a-quadro navigation, dark veil + zoom on
// the focused panel, staggered bubble-pop/text-in entrance).
//
// The chrome (top bar labels, buttons) is intentionally NOT wired through
// i18next — the exported file is a standalone artifact with no JS runtime
// dependency on the app, so its UI strings are fixed PT-BR literals (matching
// the app's own default fallback text for these same labels).
const READER_LABELS = {
  title: 'Leitura',
  rtlBadge: '← direita p/ esquerda',
  guided: 'Quadro a quadro',
  forward: 'Avançar',
  back: 'Voltar',
  animations: 'Animações',
  panelCounter: 'Quadro {{n}}/{{m}}',
} as const;

const READER_CSS = `
*{box-sizing:border-box;}
html,body{margin:0;padding:0;height:100%;background:#0b0b0f;color:#fff;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;overflow:hidden;}
#app{position:fixed;inset:0;display:flex;flex-direction:column;}
.topbar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 16px;flex-wrap:wrap;}
.topbar .title{display:flex;align-items:center;gap:8px;font-family:ui-monospace,"JetBrains Mono",monospace;font-size:10px;text-transform:uppercase;letter-spacing:.2em;opacity:.8;}
.badge{background:rgba(255,255,255,.1);border-radius:4px;padding:2px 6px;letter-spacing:.05em;}
.controls{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.btn{background:rgba(255,255,255,.1);border:none;color:#fff;border-radius:8px;padding:5px 9px;font-family:ui-monospace,"JetBrains Mono",monospace;font-size:10px;text-transform:uppercase;letter-spacing:.08em;cursor:pointer;transition:background .15s;}
.btn:hover{background:rgba(255,255,255,.2);}
.btn.active{background:#4F66A8;}
.counter{background:rgba(255,255,255,.1);border-radius:4px;padding:3px 6px;font-family:ui-monospace,"JetBrains Mono",monospace;font-size:10px;}
.pagecount{font-family:ui-monospace,"JetBrains Mono",monospace;font-size:12px;}
.stage{position:relative;flex:1;min-height:0;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:8px;perspective:2000px;}
.page-wrap{position:relative;height:100%;width:auto;max-width:100%;background:#fff;box-shadow:0 20px 60px rgba(0,0,0,.55);transition:transform .85s cubic-bezier(.65,0,.35,1);}
@media (max-width:767px){.page-wrap{width:min(96vw,560px);height:auto;}}
.page-wrap svg{display:block;width:100%;height:100%;}
.zone{position:absolute;top:0;bottom:0;width:33.333%;background:transparent;border:none;padding:0;cursor:pointer;z-index:5;-webkit-tap-highlight-color:transparent;}
.zone-right{right:0;}
.zone-left{left:0;}
.progress{height:4px;width:100%;background:rgba(255,255,255,.1);}
.progress-bar{height:100%;width:0;background:#4F66A8;transition:width .2s;}
.bubble-text{white-space:normal;}
.bubble{transform-box:view-box;}
.bubble-fuse{pointer-events:none;}
.bubble,.bubble-fuse{transition:opacity .18s ease-out;}
.bubble-hidden{opacity:0;}
.veil-fill{fill:rgba(2,2,4,.97);transition:d .85s cubic-bezier(.65,0,.35,1),opacity .4s ease;opacity:0;}
.veil-fill.on{opacity:1;}
.veil-outline{fill:none;stroke:rgba(255,255,255,.55);stroke-width:2;vector-effect:non-scaling-stroke;transition:d .85s cubic-bezier(.65,0,.35,1),opacity .4s ease;opacity:0;}
.veil-outline.on{opacity:1;}
.bubble.anim-pop{animation:bubble-pop .4s cubic-bezier(.34,1.56,.64,1) both;}
.bubble-text.anim-text{animation:bubble-text-in .3s ease both;}
@keyframes bubble-pop{0%{opacity:0;transform:scale(.55);}70%{opacity:1;transform:scale(1.06);}100%{opacity:1;transform:scale(1);}}
@keyframes bubble-text-in{from{opacity:0;transform:translateY(2px);}to{opacity:1;transform:translateY(0);}}
/* Page-turn (Moleskine-notebook 3D flip) — mirrors .manga-flip-* in the
   app's own styles.css 1:1. .manga-flip-stage stacks the OUTGOING page's own
   live .page-wrap (reparented, never rasterized) inside a rotating
   .manga-flip-sheet over the INCOMING .page-wrap underneath (already in
   normal flow) — see startFlip in the runtime below, which reuses that same
   .page-wrap for sizing/background/shadow. These rules only own the STAGE
   LAYOUT (stacking/centering/face visibility); the actual transform/
   box-shadow/shade numbers are applied inline by runPageFlip
   (src/lib/page-flip.ts, concatenated ahead of this script). */
.manga-flip-stage{position:absolute;inset:0;z-index:10;perspective:1200px;pointer-events:none;}
.manga-flip-slot{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;}
.manga-flip-under{z-index:1;}
.manga-flip-sheet-slot{z-index:2;}
.manga-flip-sheet{transform-style:preserve-3d;will-change:transform,box-shadow;}
.manga-flip-face{position:absolute;inset:0;backface-visibility:hidden;overflow:hidden;border-radius:inherit;background:#fff;}
.manga-flip-face-back{transform:rotateY(180deg);background:#f4f0e8;}
.manga-flip-shade{position:absolute;inset:0;pointer-events:none;opacity:0;}
`;

/**
 * The reader's client-side logic — a plain, self-contained function (no
 * closures over module state) that gets embedded into the exported file via
 * `Function.prototype.toString()` and invoked inline (see `buildHtmlDocument`).
 * Because it is stringified rather than transcribed by hand into a template
 * literal, there is zero risk of backtick/`${}` escaping bugs between this
 * module's own template literals and the reader's script — and TypeScript
 * still fully type-checks the function body here, before it ever leaves the app.
 */
function readerRuntime(
  DATA: { width: number; height: number; panels: { points: { x: number; y: number }[] }[] }[],
  rtl: boolean,
): void {
  // Timing constants (ms) — 1:1 twins of the internal reader's own
  // `READER_*`/`READ_*` constants in manga-editor.tsx (kept as a sibling
  // copy, same convention as `computeBubbleGeometryPx`: this function is
  // stringified into a standalone <script>, it cannot import anything).
  const POP_MS = 400, TEXT_IN_DELAY_MS = 220, GAP_MS = 200, QUEUE_BASE_MS = 150;
  const READ_BASE_MS = 450, READ_PER_CHAR_MS = 14, READ_CAP_MS = 1600;
  const FADE_OUT_MS = 180; // how long a just-departed panel's balloons take to fade away (see replayBubbles)
  const FLIP_MS = 700; // MUST match `READER_FLIP_MS` in manga-editor.tsx — passed as `durationMs` to every `runPageFlip` call

  // `panel` also uses the -1/panelCount SENTINELS the in-app reader's
  // `readerStep` does (see its comment) — both mean "whole page", but at
  // opposite edges, so a second step in the same direction can tell "pause
  // here" from "already paused, now turn the page" apart. `turning` blocks
  // all navigation input while a page-flip animation is in flight.
  // `navFlip` (advance on the LEFT) is SERIALIZED from the project's reading
  // direction — RTL manga advances left, LTR comics/HQ advance right — so the
  // exported reader opens consistent with the field, no local override.
  const state = { page: 0, panel: null as number | null, guided: true, navFlip: rtl, anim: true, turning: false };
  try {
    const storedAnim = localStorage.getItem('manga-reader-anim');
    if (storedAnim !== null) state.anim = storedAnim !== '0';
  } catch { /* ignore */ }

  const totalPages = DATA.length;
  const pageCounterEl = document.getElementById('page-counter');
  const panelCounterEl = document.getElementById('panel-counter');
  const guidedBtn = document.getElementById('btn-guided');
  const animBtn = document.getElementById('btn-anim');
  const progressBar = document.getElementById('progress-bar');
  const stageEl = document.querySelector('.stage') as HTMLElement | null;
  const labelPanelCounter = (panelCounterEl && panelCounterEl.getAttribute('data-template')) || 'Quadro {{n}}/{{m}}';

  /** Only an IN-RANGE panel index counts as "focused" — the -1/cnt sentinels
   * both mean "whole page" (see the `state` comment above). */
  function isValidPanel(idx: number | null, cnt: number): idx is number {
    return idx !== null && idx >= 0 && idx < cnt;
  }

  /** Read-time (ms) a bubble holds the "current" slot before the next one
   * pops in — proportional to its FULL text's length (no typewriter), 1:1
   * twin of `readDurationMs` in manga-editor.tsx. */
  function readDurationMs(text: string): number {
    return Math.min(READ_CAP_MS, READ_BASE_MS + text.length * READ_PER_CHAR_MS);
  }

  function updateUI(): void {
    if (pageCounterEl) pageCounterEl.textContent = (state.page + 1) + ' / ' + totalPages;
    const cnt = DATA[state.page].panels.length;
    if (panelCounterEl) {
      if (state.guided && isValidPanel(state.panel, cnt)) {
        panelCounterEl.style.display = '';
        panelCounterEl.textContent = labelPanelCounter
          .replace('{{n}}', String(state.panel + 1))
          .replace('{{m}}', String(cnt));
      } else {
        panelCounterEl.style.display = 'none';
      }
    }
    if (guidedBtn) guidedBtn.classList.toggle('active', state.guided);
    if (animBtn) animBtn.classList.toggle('active', state.anim);
    if (progressBar) {
      progressBar.style.width = ((state.page + 1) / totalPages * 100) + '%';
      progressBar.style.marginLeft = state.navFlip ? 'auto' : '';
    }
  }

  // ── Sequential bubble entrance queue (pop + fade-in, no typewriter) ───────
  // Every group that was VISIBLE (part of the previously-active scene) —
  // tracked here, across calls, so a real "leaving a focused panel" can be
  // told apart from "was already hidden" / "never shown yet" (first load) —
  // see the `leaving`/`wasVisible` split in `replayBubbles` below. Every
  // element in this animation is purely CSS `animation`/`transition`-driven;
  // no per-frame JS timers are needed anywhere in this queue anymore.
  let lastVisibleGroups: HTMLElement[] = [];

  function replayBubbles(pageIdx: number, panelIdx: number | null): void {
    const wrap = document.getElementById('page-wrap-' + pageIdx);
    if (!wrap) return;
    const cnt = DATA[pageIdx].panels.length;
    const focusedValid = isValidPanel(panelIdx, cnt);

    // OWNERSHIP: the focused panel's own bubbles are re-parented ABOVE the
    // veil layer (stay bright even overflowing into neighbours); everyone
    // else goes back below it, in original DOM order (data-oidx).
    const svg = wrap.querySelector('svg');
    const veil = wrap.querySelector('.veil-layer');
    if (svg && veil) {
      const all = Array.from(wrap.querySelectorAll('.bubble, .bubble-fuse')) as HTMLElement[];
      all
        .sort((a, b) => Number(a.getAttribute('data-oidx') ?? 0) - Number(b.getAttribute('data-oidx') ?? 0))
        .forEach((el) => { svg.insertBefore(el, veil); });
      if (focusedValid) {
        all
          .filter((el) => el.getAttribute('data-panel') === String(panelIdx))
          .sort((a, b) => Number(a.getAttribute('data-oidx') ?? 0) - Number(b.getAttribute('data-oidx') ?? 0))
          .forEach((el) => { svg.appendChild(el); });
      }
    }

    const groups = Array.from(wrap.querySelectorAll('.bubble, .bubble-fuse')) as HTMLElement[];
    // Guided focus: every balloon OUTSIDE the focused panel is hidden — only
    // the panel being read has balloons on screen (their ART stays visible,
    // darkened, under the veil as before). Whole-page view (`focusedValid`
    // false) keeps everyone in the scene.
    const inScene = groups.filter((g) => !focusedValid || g.getAttribute('data-panel') === String(panelIdx));
    const inSceneSet = new Set(inScene);
    const lastVisibleSet = new Set(lastVisibleGroups);

    groups.filter((g) => !inSceneSet.has(g)).forEach((g) => {
      if (lastVisibleSet.has(g)) {
        // Genuinely LEAVING a scene it was just shown in — fade out for
        // real. A finished entrance animation is dropped FIRST (a still-
        // applied CSS Animation would otherwise keep owning `opacity`, and
        // `bubble-hidden`'s transition below would never actually run).
        g.classList.remove('anim-pop');
        g.style.animationDelay = '';
        const textEl = g.querySelector('.bubble-text') as HTMLElement | null;
        if (textEl) { textEl.classList.remove('anim-text'); textEl.style.animationDelay = ''; }
        void g.offsetWidth; // commit the "no animation" state before...
        g.classList.add('bubble-hidden'); // ...this genuinely transitions (see `.bubble{transition:...}` below)
      } else if (!g.classList.contains('bubble-hidden')) {
        // Never shown yet (fresh page) — hide instantly, no fade (there's
        // nothing to fade FROM that the reader ever actually saw).
        g.style.transition = 'none';
        g.classList.add('bubble-hidden');
        void g.offsetWidth;
        g.style.transition = '';
      }
      // else: already hidden and staying hidden — left completely untouched.
    });

    // Reset every IN-SCENE group — clear any leftover animation state from a
    // previous visit to this exact scene before (re)popping it in.
    inScene.forEach((g) => {
      g.classList.remove('anim-pop', 'bubble-hidden');
      g.style.animationDelay = '';
      const textEl = g.querySelector('.bubble-text') as HTMLElement | null;
      if (textEl) { textEl.classList.remove('anim-text'); textEl.style.animationDelay = ''; }
    });
    lastVisibleGroups = inScene;

    // `.bubble-fuse` fill-only patches never animate themselves (mirrors the
    // in-app reader's `fillOnly` bubbles) — they just followed their group
    // above; only real `.bubble` groups drive the pop/fade-in queue.
    const queueGroups = inScene.filter((g) => g.classList.contains('bubble'));

    // The entrance QUEUE only ever runs for a FOCUSED panel (guided "quadro
    // a quadro" mode) — the whole page (focusedValid=false: unguided mode,
    // first render, or the page just landed on after a turn) always shows
    // EVERY balloon instantly, full text, no entrance animation, regardless
    // of `state.anim`.
    if (!state.anim || !focusedValid) {
      // No queue — show everything immediately, full text.
      queueGroups.forEach((g) => {
        const textEl = g.querySelector('.bubble-text') as HTMLElement | null;
        if (textEl) textEl.textContent = textEl.getAttribute('data-text') || '';
      });
      return;
    }

    // Bubbles pop in ONE AT A TIME, in reading order (DOM/array order = the
    // page's own reading order) — each bubble's FULL text fades in
    // immediately (no progressive typewriter reveal) and holds for
    // `readDurationMs` before the next bubble's pop starts. 'sfx' bubbles
    // have no text layer at all, so they just hand off to the next bubble as
    // soon as their own pop finishes.
    let cursor = QUEUE_BASE_MS;
    queueGroups.forEach((g) => {
      void g.offsetWidth; // force reflow so the pop animation restarts even if re-added unchanged
      g.style.animationDelay = (cursor / 1000) + 's';
      g.classList.add('anim-pop');
      const textEl = g.querySelector('.bubble-text') as HTMLElement | null;
      if (!textEl) { cursor += POP_MS + GAP_MS; return; } // 'sfx' — no text layer at all
      const fullText = textEl.getAttribute('data-text') || '';
      textEl.textContent = fullText;
      const typingStart = cursor + TEXT_IN_DELAY_MS;
      textEl.style.animationDelay = (typingStart / 1000) + 's';
      textEl.classList.add('anim-text');
      cursor = typingStart + readDurationMs(fullText) + GAP_MS;
    });
  }

  /**
   * Moleskine-style 3D page-turn: REPARENTS the OUTGOING page's own live
   * `.page-wrap` (never rasterized, never rebuilt — the exact DOM node
   * already on screen) into a rotating `.manga-flip-sheet`'s front face,
   * stacked over the INCOMING `.page-wrap` (already visible underneath, in
   * normal flow, from the very first synchronous step of this function — no
   * async work happens before something is on screen, unlike the old
   * rasterized curl this replaces — see `runPageFlip` in page-flip.ts,
   * concatenated into this same inline `<script>`, for why that mattered).
   * Restores both `.page-wrap`s to normal flow and calls `onDone` (which
   * commits the real page/focus change) once the animation completes. Skips
   * the flip outright (instant cut) for `prefers-reduced-motion` or the
   * animations toggle being off.
   */
  function startFlip(fromIdx: number, toIdx: number, dir: 1 | -1, onDone: () => void): void {
    const fromWrap = document.getElementById('page-wrap-' + fromIdx);
    const toWrap = document.getElementById('page-wrap-' + toIdx);
    if (!stageEl || !fromWrap || !toWrap) { onDone(); return; }
    state.turning = true;

    const finishTurn = (): void => { state.turning = false; onDone(); };
    const reduced = !state.anim || (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    if (reduced) { finishTurn(); return; }

    try {
      // Measure BEFORE reparenting (while still in normal flow) — the sheet
      // is given this exact pixel box so it lands in precisely the same
      // spot a live page-wrap would occupy.
      const rect = fromWrap.getBoundingClientRect();
      const boxW = rect.width, boxH = rect.height;

      const overlay = document.createElement('div');
      overlay.className = 'manga-flip-stage';

      const underSlot = document.createElement('div');
      underSlot.className = 'manga-flip-slot manga-flip-under';
      toWrap.style.display = '';
      underSlot.appendChild(toWrap);

      const sheetSlot = document.createElement('div');
      sheetSlot.className = 'manga-flip-slot manga-flip-sheet-slot';
      // Reuses the `.page-wrap` class itself (same background/box-shadow/
      // sizing rules every page already has) so the sheet's resting look is
      // identical to a normal page — `runPageFlip` overrides box-shadow/
      // transform/border-radius inline once the turn actually starts.
      const sheet = document.createElement('div');
      sheet.className = 'page-wrap manga-flip-sheet';
      sheet.style.cssText = 'position:relative;width:' + boxW + 'px;height:' + boxH + 'px;';

      const front = document.createElement('div');
      front.className = 'manga-flip-face manga-flip-face-front';
      // Reparent the OUTGOING page's real, already-rendered node — pinned to
      // fill the face exactly (overrides its normal `.page-wrap` sizing,
      // restored on `restore()` below).
      fromWrap.style.position = 'absolute';
      fromWrap.style.inset = '0';
      fromWrap.style.width = '100%';
      fromWrap.style.height = '100%';
      front.appendChild(fromWrap);
      const shade = document.createElement('div');
      shade.className = 'manga-flip-shade';
      front.appendChild(shade);

      const back = document.createElement('div');
      back.className = 'manga-flip-face manga-flip-face-back';

      sheet.appendChild(front);
      sheet.appendChild(back);
      sheetSlot.appendChild(sheet);
      overlay.appendChild(underSlot);
      overlay.appendChild(sheetSlot);
      stageEl.appendChild(overlay);

      const restore = (): void => {
        fromWrap.style.position = '';
        fromWrap.style.inset = '';
        fromWrap.style.width = '';
        fromWrap.style.height = '';
        stageEl!.appendChild(fromWrap);
        stageEl!.appendChild(toWrap);
        overlay.remove();
        finishTurn();
      };

      runPageFlip(sheet, toWrap, shade, { dir, durationMs: FLIP_MS }, restore);
    } catch {
      // Never get stuck mid-navigation — corte seco imediato, commit right away.
      finishTurn();
    }
  }

  function applyFocus(pageIdx: number, panelIdx: number | null): void {
    state.page = pageIdx;
    state.panel = panelIdx;
    document.querySelectorAll('.page-wrap').forEach((el) => {
      (el as HTMLElement).style.display = el.id === 'page-wrap-' + pageIdx ? '' : 'none';
    });
    const pageData = DATA[pageIdx];
    const cnt = pageData.panels.length;
    const wrap = document.getElementById('page-wrap-' + pageIdx);
    const focused = isValidPanel(panelIdx, cnt) ? pageData.panels[panelIdx] : null;
    if (wrap) {
      if (focused) {
        const xs = focused.points.map((p) => p.x), ys = focused.points.map((p) => p.y);
        const bw = Math.max(0.05, Math.max(...xs) - Math.min(...xs));
        const bh = Math.max(0.05, Math.max(...ys) - Math.min(...ys));
        const cx = (Math.max(...xs) + Math.min(...xs)) / 2;
        const cy = (Math.max(...ys) + Math.min(...ys)) / 2;
        const s = Math.min(3, Math.min(1 / bw, 1 / bh) * 0.92);
        wrap.style.transform = 'scale(' + s + ') translate(' + ((0.5 - cx) * 100) + '%, ' + ((0.5 - cy) * 100) + '%)';
      } else {
        wrap.style.transform = 'none';
      }
    }
    const veilFill = document.getElementById('veil-fill-' + pageIdx);
    const veilOutline = document.getElementById('veil-outline-' + pageIdx);
    if (veilFill && veilOutline) {
      if (focused) {
        // Resample the hole to a FIXED vertex count so CSS can morph 'd'
        // smoothly between panels (same trick as the in-app reader).
        // Corner-preserving resample: every ORIGINAL vertex is kept exactly
        // (no chamfered frames), extras distributed by edge length, total = N.
        const N = 28;
        const pts = focused.points;
        const k = pts.length;
        const lens: number[] = [];
        let per = 0;
        for (let i = 0; i < k; i++) {
          const a = pts[i], b = pts[(i + 1) % k];
          const len = Math.hypot(b.x - a.x, b.y - a.y);
          lens.push(len); per += len;
        }
        const extraTotal = Math.max(0, N - k);
        const exact = lens.map((l) => (per > 0 ? (l / per) * extraTotal : 0));
        const alloc = exact.map(Math.floor);
        let leftover = extraTotal - alloc.reduce((a, b) => a + b, 0);
        const order = exact.map((e, i) => ({ frac: e - Math.floor(e), i })).sort((a, b) => b.frac - a.frac);
        for (let j = 0; j < order.length && leftover > 0; j++, leftover--) alloc[order[j].i]++;
        const hole: { x: number; y: number }[] = [];
        for (let i = 0; i < k; i++) {
          const a = pts[i], b = pts[(i + 1) % k];
          hole.push({ x: a.x, y: a.y });
          for (let j = 1; j <= alloc[i]; j++) {
            const t = j / (alloc[i] + 1);
            hole.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
          }
        }
        const W = pageData.width, H = pageData.height;
        const holeD = hole.map((p) => (p.x * W).toFixed(2) + ' ' + (p.y * H).toFixed(2)).join(' L ');
        (veilFill as HTMLElement).style.setProperty('d', 'path("M0 0 H' + W + ' V' + H + ' H0 Z M ' + holeD + ' Z")');
        (veilOutline as HTMLElement).style.setProperty('d', 'path("M ' + holeD + ' Z")');
        veilFill.classList.add('on');
        veilOutline.classList.add('on');
      } else {
        veilFill.classList.remove('on');
        veilOutline.classList.remove('on');
      }
    }
    replayBubbles(pageIdx, panelIdx);
    updateUI();
  }

  // Crosses to the next/previous PAGE (page-turn animation), landing on the
  // destination's whole-page edge OPPOSITE the arrival direction — arriving
  // forward lands on the "entry" edge (next step reads panel 0); arriving
  // backward lands on the "exit" edge (next step reads the LAST panel). A
  // no-op at either end of the book (nothing to turn to).
  function crossPage(dir: 1 | -1): void {
    const np = state.page + dir;
    if (np < 0 || np >= totalPages) return;
    const ncnt = DATA[np].panels.length;
    const landing = state.guided && ncnt > 0 ? (dir === 1 ? -1 : ncnt) : null;
    // Spine side follows the reading direction: RTL manga mirrors the hinge
    // (spine on the RIGHT) so a forward turn lifts from the correct edge.
    const hinge = (dir * (rtl ? -1 : 1)) as 1 | -1;
    startFlip(state.page, np, hinge, () => applyFocus(np, landing));
  }

  // One step of reading — see `readerStep`'s state-machine comment in
  // manga-editor.tsx for the full quadro-by-quadro → whole-page → turn
  // sequence; this is its 1:1 twin. Ignored entirely while a page-turn
  // animation is in flight (`state.turning`).
  function step(delta: 1 | -1): void {
    if (state.turning) return;
    const cnt = DATA[state.page].panels.length;
    if (state.guided && cnt > 0) {
      const cur = state.panel !== null ? state.panel : (delta === 1 ? -1 : cnt); // fresh entry → normalize to the correct edge
      const next = cur + delta;
      if (next >= 0 && next < cnt) { applyFocus(state.page, next); return; } // move within panels
      if (next === cnt || next === -1) { applyFocus(state.page, next); return; } // reached a whole-page edge — pause here first
      crossPage(delta); // already AT the edge — advance for real, turn the page
    } else {
      crossPage(delta);
    }
  }

  if (guidedBtn) guidedBtn.addEventListener('click', () => { state.guided = !state.guided; applyFocus(state.page, null); });
  if (animBtn) animBtn.addEventListener('click', () => {
    state.anim = !state.anim;
    try { localStorage.setItem('manga-reader-anim', state.anim ? '1' : '0'); } catch { /* ignore */ }
    replayBubbles(state.page, state.panel);
    updateUI();
  });
  const zoneRight = document.getElementById('zone-right');
  const zoneLeft = document.getElementById('zone-left');
  if (zoneRight) zoneRight.addEventListener('click', () => step(state.navFlip ? -1 : 1));
  if (zoneLeft) zoneLeft.addEventListener('click', () => step(state.navFlip ? 1 : -1));
  window.addEventListener('keydown', (e) => {
    const fwdKey = state.navFlip ? 'ArrowLeft' : 'ArrowRight';
    const backKey = state.navFlip ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === fwdKey) step(1);
    else if (e.key === backKey) step(-1);
  });

  applyFocus(0, null);
}

/** Assembles the final standalone document: head (fonts/CSS), reader chrome,
 * every page's SVG, and the inline runtime script (see `readerRuntime`). */
function buildHtmlDocument(
  title: string,
  dir: 'rtl' | 'ltr',
  pagesHtml: string,
  pagesData: { width: number; height: number; panels: { points: Point[] }[] }[],
): string {
  const escapedTitle = escapeHtml(title);
  const runtimeSrc = readerRuntime.toString();
  // `runPageFlip` is stringified as a plain hoisted function DECLARATION,
  // concatenated ahead of the `readerRuntime` IIFE in the SAME inline
  // <script> — so the bare `runPageFlip(...)` call inside `startFlip`
  // resolves via that shared script-global scope at runtime, exactly as it
  // does via the module-level import when this file's own TS is type-checked.
  const flipSrc = runPageFlip.toString();
  // Defensive: JSON can never legally contain "</", but escaping it (as <)
  // guarantees the HTML parser can never mistake it for a real closing tag.
  const dataJson = JSON.stringify(pagesData).replace(/</g, '\\u003c');
  const script = `<script>${flipSrc}\n(${runtimeSrc})(${dataJson}, ${dir === 'rtl'});<\/script>`;

  const rtlBadgeHtml = dir === 'rtl' ? ` <span class="badge">${escapeHtml(READER_LABELS.rtlBadge)}</span>` : '';

  const topbar = `<div class="topbar">`
    + `<span class="title">${escapeHtml(READER_LABELS.title)}${rtlBadgeHtml}</span>`
    + `<div class="controls">`
    + `<span id="panel-counter" class="counter" data-template="${escapeHtml(READER_LABELS.panelCounter)}" style="display:none"></span>`
    + `<button id="btn-guided" class="btn active" type="button">${escapeHtml(READER_LABELS.guided)}</button>`
    + `<button id="btn-anim" class="btn active" type="button">${escapeHtml(READER_LABELS.animations)}</button>`
    + `<span id="page-counter" class="pagecount"></span>`
    + `</div></div>`;

  const stage = `<div class="stage">${pagesHtml}`
    + `<button id="zone-right" class="zone zone-right" type="button" aria-label="${escapeHtml(READER_LABELS.forward)}"></button>`
    + `<button id="zone-left" class="zone zone-left" type="button" aria-label="${escapeHtml(READER_LABELS.back)}"></button>`
    + `</div>`;

  const progress = `<div class="progress"><div id="progress-bar" class="progress-bar"></div></div>`;

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${escapedTitle}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Comic+Neue:wght@400;700&family=Bangers&family=Fraunces:wght@400;700&display=swap" rel="stylesheet" />
<style>${READER_CSS}</style>
</head>
<body>
<div id="app">
${topbar}
${stage}
${progress}
</div>
${script}
</body>
</html>`;
}

/**
 * Exports the whole manga as a single, offline, self-contained interactive
 * HTML file — real SVG panels/bubbles (fidelity with the editor/PNG export)
 * plus an embedded reader mirroring the in-app guided preview (quadro-a-quadro
 * navigation, veil+zoom, staggered bubble entrance animations).
 */
export async function exportHtml(
  pages: MangaPage[],
  title = 'manga',
  readingDirection: 'rtl' | 'ltr' = 'rtl',
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  if (pages.length === 0) throw new Error('Não há páginas para exportar.');
  try {
    const imageCache = new Map<string, Promise<string>>();
    const pagesHtmlParts: string[] = [];
    const pagesData: { width: number; height: number; panels: { points: Point[] }[] }[] = [];
    for (let i = 0; i < pages.length; i++) {
      const { html, panelsData, width, height } = await buildPageMarkup(pages[i], i, imageCache);
      pagesHtmlParts.push(html);
      pagesData.push({ width, height, panels: panelsData });
      onProgress?.(i + 1, pages.length);
    }
    const doc = buildHtmlDocument(title, readingDirection, pagesHtmlParts.join(''), pagesData);
    const filename = title.toLowerCase().endsWith('.html') ? title : `${title}.html`;
    downloadBlob(new Blob([doc], { type: 'text/html' }), filename);
  } catch (err) {
    throw new Error(err instanceof Error ? `Falha ao exportar HTML: ${err.message}` : 'Falha ao exportar HTML.');
  }
}
